import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { seatNameBoxClass, TableSeat, type TableSeatProps } from "../game/TableSeat";
import { fa } from "../i18n/fa";

/* ------------------------------------------------------------------ *
 * A SIDE SEAT'S NOTICE ON TWO LINES AT MOST (P1 Task 9b — پاسور's Task 8b approach, not its file). A
 * side seat's notice wraps in its box (`TableSeat`'s `wrapNotice`, capped at `SIDE_NAME_MAX`: 50 px at
 * 320, 41 at 300 — the side card's room), and the full matrix measured «در حال اتصال» on 3–4 lines
 * there (320×568 ×1.3 / ×1.5, 300×667 ×1.15–×1.5: 59–100 px tall — the seat's bid badge lifted onto
 * the felt's ring) and «از میز رفت» on 4 (300×667 ×1.3 / ×1.5). Where a notice has SHORT forms
 * (`SIDE_NOTICE_SHORT`) and the whole one would take a third line, the seat paints the richest short
 * form that takes ONE line — each picked by HEIGHT against an invisible copy of the seat's own notice
 * box (two lines for the whole form, one for a short one), out of flow and unheard; no form on one
 * line: the last. The notice's label (the whole sentence) is untouched, and so is `TableSeat`.
 * ------------------------------------------------------------------ */

/** The short forms, richest first, by the whole notice's painted text. */
export const SIDE_NOTICE_SHORT: Readonly<Record<string, readonly string[]>> = {
  [fa.online.seatReconnecting]: [fa.shelem.noticeReconnecting, fa.shelem.noticeReconnectingWord],
  [fa.online.seatLeft]: [fa.shelem.noticeLeft],
};
/** `SeatNoticeText`'s own type: the copies must break where the notice does. */
export const NOTICE_TYPE = { fontSize: 12, fontWeight: 700, letterSpacing: 0 } as const;

/**
 * The index of the first form copy (`copies`, richest first) no taller than the room copy inside the
 * box (`room`) — or the number of copies when none is. Read off the laid-out boxes before the first
 * paint, and again when the box resizes or a web font lands; unlaid-out (happy-dom), every height is 0:
 * the richest form.
 */
function useTallestFit(
  box: { current: HTMLElement | null },
  key: string | null,
  copies: string,
  room: string,
): number {
  const [fit, setFit] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (key === null || !el) return undefined;
    const read = (): void => {
      const roomEl = el.querySelector<HTMLElement>(room);
      const size = roomEl ? roomEl.getBoundingClientRect().height : 0;
      const forms = [...el.querySelectorAll<HTMLElement>(copies)];
      const i = forms.findIndex((c) => c.getBoundingClientRect().height <= size);
      setFit(i < 0 ? forms.length : i);
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [box, key, copies, room]);
  return fit;
}

/** A side seat: `TableSeat` with its wrapped notice held to two lines (above). */
export function ShelemSideSeat(props: TableSeatProps): ReactNode {
  const { notice = null, compact = false, nameSkin, nameMax } = props;
  const shorts = notice !== null ? SIDE_NOTICE_SHORT[notice.text] : undefined;
  const key = notice !== null && shorts !== undefined ? [notice.text, ...shorts].join("\n") : null;
  const boxRef = useRef<HTMLDivElement>(null);
  const whole = useTallestFit(boxRef, key, '[data-notice-form="whole"]', '[data-notice-room="2"]');
  const short = useTallestFit(boxRef, key, '[data-notice-form="short"]', '[data-notice-room="1"]');
  const shown =
    notice !== null && shorts !== undefined && whole > 0
      ? { ...notice, text: shorts[Math.min(short, shorts.length - 1)]! }
      : notice;
  const box = seatNameBoxClass(nameSkin, compact, true, true);
  return (
    <>
      <TableSeat {...props} wrapNotice notice={shown} />
      {notice !== null && shorts !== undefined ? (
        <div
          ref={boxRef}
          aria-hidden="true"
          data-notice-copies=""
          className="invisible pointer-events-none absolute start-0 top-0"
          style={{ minWidth: nameMax, maxWidth: nameMax }}
        >
          <span data-notice-room="2" className={box}>
            <span style={NOTICE_TYPE}>
              {fa.shelem.us}
              <br />
              {fa.shelem.us}
            </span>
          </span>
          <span data-notice-room="1" className={`${box} absolute start-0 top-0 w-full`}>
            <span style={NOTICE_TYPE}>{fa.shelem.us}</span>
          </span>
          {[notice.text, ...shorts].map((f, i) => (
            <span
              key={f}
              data-notice-form={i === 0 ? "whole" : "short"}
              className={`${box} absolute start-0 top-0 w-full`}
            >
              <span style={NOTICE_TYPE}>{f}</span>
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}
